// Sentinel (Keycloak) auth service. PKCE only — the realm uses Apple/Google
// identity providers and the whp-applications client has Direct Access Grants
// disabled. No password login.

const AUTH_CONFIG = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'https://sentinel.welcomehps.com/auth',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'Applications',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'whp-applications',
};

const TOKEN_ENDPOINT = `${AUTH_CONFIG.url}/realms/${AUTH_CONFIG.realm}/protocol/openid-connect/token`;
const LOGOUT_ENDPOINT = `${AUTH_CONFIG.url}/realms/${AUTH_CONFIG.realm}/protocol/openid-connect/logout`;
const AUTH_ENDPOINT = `${AUTH_CONFIG.url}/realms/${AUTH_CONFIG.realm}/protocol/openid-connect/auth`;

export const SOCIAL_PROVIDERS = {
  google: 'google',
  apple: 'apple',
} as const;
export type SocialProvider = keyof typeof SOCIAL_PROVIDERS;

const STORAGE_KEYS = {
  accessToken: 'whp_access_token',
  refreshToken: 'whp_refresh_token',
  tokenExpiry: 'whp_token_expiry',
  idToken: 'whp_id_token',
  oauthState: 'whp_oauth_state',
  oauthCodeVerifier: 'whp_oauth_code_verifier',
};

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  id_token?: string;
  scope: string;
}

export interface TokenPayload {
  sub: string;
  email?: string;
  /** OIDC claim from the "email" scope; absent when the realm does not emit it. */
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  realm_access?: { roles: string[] };
  exp: number;
  iat: number;
}

export interface AuthUser {
  sub: string;
  email?: string;
  emailVerified?: boolean;
  name?: string;
  preferredUsername?: string;
  roles: string[];
}

interface AuthError {
  error: string;
  error_description?: string;
}

function storeTokens(r: TokenResponse): void {
  const expiry = Date.now() + r.expires_in * 1000;
  localStorage.setItem(STORAGE_KEYS.accessToken, r.access_token);
  localStorage.setItem(STORAGE_KEYS.refreshToken, r.refresh_token);
  localStorage.setItem(STORAGE_KEYS.tokenExpiry, expiry.toString());
  if (r.id_token) localStorage.setItem(STORAGE_KEYS.idToken, r.id_token);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.tokenExpiry);
  localStorage.removeItem(STORAGE_KEYS.idToken);
}

const getStoredAccessToken = () => localStorage.getItem(STORAGE_KEYS.accessToken);
const getStoredRefreshToken = () => localStorage.getItem(STORAGE_KEYS.refreshToken);

function isTokenExpired(minValidity = 30): boolean {
  const exp = localStorage.getItem(STORAGE_KEYS.tokenExpiry);
  if (!exp) return true;
  return Date.now() >= parseInt(exp, 10) - minValidity * 1000;
}

function decodeToken(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    const payload = parts[1];
    if (parts.length !== 3 || !payload) return null;
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function userFromToken(token: string): AuthUser | null {
  const p = decodeToken(token);
  if (!p) return null;
  const name =
    p.name || `${p.given_name || ''} ${p.family_name || ''}`.trim() || undefined;
  const user: AuthUser = {
    sub: p.sub,
    roles: p.realm_access?.roles || [],
  };
  if (p.email !== undefined) user.email = p.email;
  if (typeof p.email_verified === 'boolean') user.emailVerified = p.email_verified;
  if (name !== undefined) user.name = name;
  if (p.preferred_username !== undefined) user.preferredUsername = p.preferred_username;
  return user;
}

export async function logout(): Promise<void> {
  const refreshToken = getStoredRefreshToken();
  clearTokens();
  if (refreshToken) {
    try {
      await fetch(LOGOUT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: AUTH_CONFIG.clientId,
          refresh_token: refreshToken,
        }),
      });
    } catch {
      // local logout is enough
    }
  }
}

let refreshPromise: Promise<boolean> | null = null;
export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return false;
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(TOKEN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: AUTH_CONFIG.clientId,
          refresh_token: refreshToken,
        }),
      });
      if (res.ok) {
        storeTokens(await res.json());
        return true;
      }
      // Only a definitive client error (400 invalid_grant = refresh token
      // expired/revoked) means the session is truly gone — clear tokens so the
      // app shows the login screen. Transient failures (5xx, 429) are NOT
      // fatal: keep the tokens so the next timer tick / tab-focus can recover.
      // This stops a single Keycloak hiccup from logging the user out.
      if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        clearTokens();
      }
      return false;
    } catch {
      // Network error (offline, laptop asleep, DNS blip): do NOT clear tokens —
      // the refresh token may still be valid once connectivity returns.
      return false;
    }
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Refresh the access token only when it's close to (or past) expiry. Cheap to
 * call frequently — on a timer AND on tab focus/visibility — because it no-ops
 * while the current token is still valid, avoiding needless refresh-token
 * rotation (and the multi-tab races that rotation can cause). Returns true if a
 * usable token is in hand afterward.
 */
export async function ensureFreshToken(): Promise<boolean> {
  const hasRefresh = !!getStoredRefreshToken();
  if (hasRefresh && !isTokenExpired(120)) return true;
  if (hasRefresh && (await refreshAccessToken())) return true;
  // No refresh token, or the refresh failed — try a silent prompt=none re-auth
  // from a still-alive Keycloak/Google/Apple session before giving up.
  return silentLogin();
}

export async function getAccessToken(): Promise<string | null> {
  const token = getStoredAccessToken();
  if (!token) return null;
  if (isTokenExpired(30)) {
    return (await refreshAccessToken()) ? getStoredAccessToken() : null;
  }
  return token;
}

export function isAuthenticated(): boolean {
  const token = getStoredAccessToken();
  const refresh = getStoredRefreshToken();
  if (!token && !refresh) return false;
  if (token && !isTokenExpired(0)) return true;
  return !!refresh;
}

export function getCurrentUser(): AuthUser | null {
  const t = getStoredAccessToken();
  return t ? userFromToken(t) : null;
}

export function hasRole(role: string): boolean {
  return !!getCurrentUser()?.roles.includes(role);
}

export function hasAnyRole(roles: string[]): boolean {
  const u = getCurrentUser();
  return !!u && roles.some((r) => u.roles.includes(r));
}

export async function initializeAuth(): Promise<AuthUser | null> {
  // Valid, non-expired access token already in hand → use it.
  if (getStoredAccessToken() && !isTokenExpired(30)) return getCurrentUser();
  // Otherwise try the refresh-token grant, then a silent SSO re-auth from a
  // still-alive session (so a returning user with a live Google/Apple session
  // is logged straight back in without clicking "Login").
  if (await ensureFreshToken()) return getCurrentUser();
  return null;
}

function randomString(length: number): string {
  const a = new Uint8Array(length);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomString(32);
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return { verifier, challenge };
}

export function getOAuthCallbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

export async function initiateSocialLogin(provider: SocialProvider): Promise<void> {
  const { verifier, challenge } = await generatePKCE();
  const state = randomString(16);
  localStorage.setItem(STORAGE_KEYS.oauthState, state);
  localStorage.setItem(STORAGE_KEYS.oauthCodeVerifier, verifier);

  const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  sessionStorage.setItem('auth:returnUrl', returnUrl);

  const params = new URLSearchParams({
    client_id: AUTH_CONFIG.clientId,
    redirect_uri: getOAuthCallbackUrl(),
    response_type: 'code',
    scope: 'openid profile email',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    kc_idp_hint: SOCIAL_PROVIDERS[provider],
  });
  window.location.href = `${AUTH_ENDPOINT}?${params.toString()}`;
}

export async function handleOAuthCallback(
  code: string,
  state: string
): Promise<{ success: true; user: AuthUser } | { success: false; error: string }> {
  const storedState = localStorage.getItem(STORAGE_KEYS.oauthState);
  if (state !== storedState) return { success: false, error: 'Invalid OAuth state' };

  const verifier = localStorage.getItem(STORAGE_KEYS.oauthCodeVerifier);
  if (!verifier) return { success: false, error: 'Missing code verifier' };

  localStorage.removeItem(STORAGE_KEYS.oauthState);
  localStorage.removeItem(STORAGE_KEYS.oauthCodeVerifier);

  try {
    const res = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: AUTH_CONFIG.clientId,
        code,
        redirect_uri: getOAuthCallbackUrl(),
        code_verifier: verifier,
      }),
    });
    if (!res.ok) {
      const e: AuthError = await res.json();
      return {
        success: false,
        error: e.error_description || e.error || 'Token exchange failed',
      };
    }
    const tokens: TokenResponse = await res.json();
    storeTokens(tokens);
    const user = userFromToken(tokens.access_token);
    if (!user) {
      clearTokens();
      return { success: false, error: 'Failed to parse user info from token' };
    }
    return { success: true, user };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Network error during OAuth',
    };
  }
}

export function isOAuthCallback(): boolean {
  const p = new URLSearchParams(window.location.search);
  return p.has('code') && p.has('state');
}

export function getOAuthCallbackParams(): { code: string; state: string } | null {
  const p = new URLSearchParams(window.location.search);
  const code = p.get('code');
  const state = p.get('state');
  return code && state ? { code, state } : null;
}

// ---------------------------------------------------------------------------
// Silent SSO (prompt=none) — refresh the session with no visible redirect
// ---------------------------------------------------------------------------

const SILENT_KEYS = {
  state: 'whp_silent_state',
  verifier: 'whp_silent_verifier',
};

let silentPromise: Promise<boolean> | null = null;

/**
 * Re-establish tokens silently from a still-alive Keycloak SSO session (or the
 * upstream Google/Apple session) using a hidden iframe + `prompt=none`. No
 * user-visible redirect or prompt. Resolves true on success; false if Keycloak
 * needs interaction (login_required) or the attempt times out — the caller then
 * falls back to the login screen.
 *
 * This is reliable here because Keycloak (sentinel.welcomehps.com) and the app
 * share the welcomehps.com site, so the iframe's session cookie is first-party
 * (not blocked by Safari ITP / third-party-cookie phase-out). Any failure just
 * returns false, so this is purely additive — it never makes auth worse.
 */
export async function silentLogin(): Promise<boolean> {
  // Not in a browser, inside the silent iframe itself, or mid-interactive
  // callback → don't start a (nested/competing) silent flow.
  if (typeof window === 'undefined' || window.parent !== window) return false;
  if (isOAuthCallback()) return false;
  if (silentPromise) return silentPromise;
  silentPromise = runSilentLogin().finally(() => {
    silentPromise = null;
  });
  return silentPromise;
}

async function runSilentLogin(): Promise<boolean> {
  let code: string | null = null;
  try {
    const { verifier, challenge } = await generatePKCE();
    const state = randomString(16);
    localStorage.setItem(SILENT_KEYS.state, state);
    localStorage.setItem(SILENT_KEYS.verifier, verifier);
    const params = new URLSearchParams({
      client_id: AUTH_CONFIG.clientId,
      redirect_uri: getOAuthCallbackUrl(),
      response_type: 'code',
      scope: 'openid profile email',
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      prompt: 'none',
    });
    code = await loadSilentIframe(`${AUTH_ENDPOINT}?${params.toString()}`, state);
  } catch {
    code = null;
  }
  const verifier = localStorage.getItem(SILENT_KEYS.verifier);
  localStorage.removeItem(SILENT_KEYS.state);
  localStorage.removeItem(SILENT_KEYS.verifier);
  if (!code || !verifier) return false;
  try {
    const res = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: AUTH_CONFIG.clientId,
        code,
        redirect_uri: getOAuthCallbackUrl(),
        code_verifier: verifier,
      }),
    });
    if (!res.ok) return false;
    storeTokens(await res.json());
    return true;
  } catch {
    return false;
  }
}

/**
 * Load `authUrl` (the prompt=none auth request) in a hidden iframe and resolve
 * with the authorization `code` — or null if Keycloak returned an error
 * (login_required) or the attempt timed out. The iframe is redirected back to
 * /auth/callback, where main.tsx relays the result via postMessage.
 */
function loadSilentIframe(authUrl: string, expectedState: string): Promise<string | null> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('title', 'silent auth');
    let settled = false;
    const finish = (code: string | null): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      resolve(code);
    };
    const onMessage = (e: MessageEvent): void => {
      if (e.origin !== window.location.origin) return;
      const data = e.data as { type?: string; search?: string } | null;
      if (!data || data.type !== 'whp-silent-auth') return;
      const p = new URLSearchParams(data.search ?? '');
      finish(p.get('state') === expectedState ? p.get('code') : null);
    };
    window.addEventListener('message', onMessage);
    const timer = window.setTimeout(() => finish(null), 8000);
    iframe.src = authUrl;
    document.body.appendChild(iframe);
  });
}
